import { useState, useCallback, useMemo, useEffect } from "react";
import { isEqual } from "lodash";
import { toast } from "@jsq/ds/core";
import { addError } from "@jsq/observability";
import { formatError } from "#common/hooks/useReportError";
import { toGlobalId } from "#utils/graphql_utils";
import { useConfigureBankAccount } from "../../../common/graphql/BankAccountConfiguration/hooks/useConfigureBankAccount";
import {
  useGetAllProvidersWithCapabilities,
  type ProviderWithCapabilities,
} from "../../../common/graphql/BankAccountConfiguration/hooks/useGetAllProvidersWithCapabilities";
import { useMapBankAccountToEntity } from "../../../common/graphql/BankAccountConfiguration/hooks/useMapBankAccountToEntity";
import { ProviderBankSource, SlideoutFormState, VerificationStatus } from "./types";
import type {
  BankAccountVerificationStatus,
  FundRecsExportStatus,
} from "#graphql/supergraphTypes";
import type { BankAccountRowItem, InvestmentEntity } from "../types";

export interface UseBankAccountConfigurationProps {
  bankAccount: BankAccountRowItem | null;
  arenaId?: number | null;
  onSave?: (formState: SlideoutFormState) => void;
  onEntitySave?: (entity: { entityId: number; entityName: string }) => void;
  onClose: () => void;
}

export interface UseBankAccountConfigurationReturn {
  initialFormValues: SlideoutFormState;
  localMappedEntity: InvestmentEntity | null;
  isEntityModalOpen: boolean;
  handleOpenEntityModal: () => void;
  handleEntityModalClose: () => void;
  handleEntityModalSave: (entityId: number | null, entityName: string) => Promise<boolean>;
  isEntityModalSaving: boolean;

  fidesBankSources: ProviderBankSource[];
  rpaBankSources: ProviderBankSource[];
  isSaving: boolean;
  isLoadingProviders: boolean;
  handleSave: (formValues: SlideoutFormState) => Promise<void>;
}

const getBankSourcesByProvider = (
  providers: ProviderWithCapabilities[],
  providerName: string,
): ProviderBankSource[] => {
  return providers
    .filter((p) => p.name.toLowerCase() === providerName.toLowerCase())
    .map((p) => ({
      providerId: p.id,
      providerName: p.name,
      bankSource: p.bankSource,
      capabilityIds: p.capabilities.map((c) => c.providerCapabilityId),
    }));
};

const mapVerificationStatusToEnum = (
  status: VerificationStatus,
): BankAccountVerificationStatus => {
  return status === "VERIFIED" ? "VERIFIED" : "UNVERIFIED";
};

const getInitialFormState = (): SlideoutFormState => ({
  selectedEntityId: null,
  fidesEnabled: false,
  fidesBankMapping: null,
  fidesVerificationStatus: "UNVERIFIED",
  rpaEnabled: false,
  rpaBankMapping: null,
  fundRecsEnabled: false,
  fundRecsUdf: "",
});

const normalizeBankSource = (name: string | null | undefined): string => {
  if (!name) return "";
  return name.toLowerCase().replace(/_/g, " ");
};

const findMatchingBankSource = (
  existingName: string | null | undefined,
  bankSources: ProviderBankSource[],
): string | null => {
  if (!existingName) return null;
  const normalizedExisting = normalizeBankSource(existingName);
  const match = bankSources.find(
    (bs) => normalizeBankSource(bs.bankSource) === normalizedExisting,
  );
  return match?.bankSource || null;
};

const buildProvidersArray = (
  formValues: SlideoutFormState,
  fidesBankSources: ProviderBankSource[],
  rpaBankSources: ProviderBankSource[],
): Array<{ providerId: number }> => {
  const providersList: Array<{ providerId: number }> = [];

  if (formValues.fidesEnabled && formValues.fidesBankMapping) {
    const fidesSource = fidesBankSources.find(
      (s) => s.bankSource === formValues.fidesBankMapping,
    );
    if (fidesSource) {
      providersList.push({ providerId: fidesSource.providerId });
    }
  }

  if (formValues.rpaEnabled && formValues.rpaBankMapping) {
    const rpaSource = rpaBankSources.find((s) => s.bankSource === formValues.rpaBankMapping);
    if (rpaSource) {
      providersList.push({ providerId: rpaSource.providerId });
    }
  }

  return providersList;
};

const buildVerificationsArray = (
  formValues: SlideoutFormState,
  fidesBankSources: ProviderBankSource[],
): Array<{ providerId: number; verificationStatus: BankAccountVerificationStatus }> => {
  if (!formValues.fidesEnabled) {
    return [];
  }

  const verifications: Array<{
    providerId: number;
    verificationStatus: BankAccountVerificationStatus;
  }> = [];

  if (formValues.fidesBankMapping) {
    const fidesSource = fidesBankSources.find(
      (s) => s.bankSource === formValues.fidesBankMapping,
    );
    if (fidesSource) {
      verifications.push({
        providerId: fidesSource.providerId,
        verificationStatus: mapVerificationStatusToEnum(formValues.fidesVerificationStatus),
      });
    }
  }

  return verifications;
};

const buildConfigurationsArray = (
  formValues: SlideoutFormState,
  selectedProviderIds: number[],
  allProviders: ProviderWithCapabilities[],
): Array<{
  providerCapabilityId: number;
  fundRecsExportStatus: FundRecsExportStatus;
  fundRecsUdf: string | null;
}> => {
  if (!formValues.fundRecsEnabled) {
    return [];
  }

  const configurations: Array<{
    providerCapabilityId: number;
    fundRecsExportStatus: FundRecsExportStatus;
    fundRecsUdf: string | null;
  }> = [];
  const fundRecsExportStatus: FundRecsExportStatus = "ENABLED";
  const fundRecsUdf = formValues.fundRecsUdf || null;

  const capabilityIds: number[] = [];

  selectedProviderIds.forEach((providerId) => {
    const provider = allProviders.find((p) => p.id === providerId);
    if (provider) {
      capabilityIds.push(...provider.capabilities.map((c) => c.providerCapabilityId));
    }
  });

  const uniqueCapabilityIds = Array.from(new Set(capabilityIds));

  uniqueCapabilityIds.forEach((capabilityId) => {
    configurations.push({
      providerCapabilityId: capabilityId,
      fundRecsExportStatus,
      fundRecsUdf,
    });
  });

  return configurations;
};

export const useBankAccountConfiguration = ({
  bankAccount,
  arenaId,
  onSave,
  onEntitySave,
  onClose,
}: UseBankAccountConfigurationProps): UseBankAccountConfigurationReturn => {
  const [isEntityModalOpen, setIsEntityModalOpen] = useState(false);

  // Kept separate from bankAccount so updates don't change initialFormValues and trigger
  // Formik's enableReinitialize, which would reset unsaved config progress.
  const [localMappedEntity, setLocalMappedEntity] = useState<InvestmentEntity | null>(
    () => bankAccount?.mappedInvestmentEntities?.[0] ?? null,
  );

  const bankAccountId = bankAccount?.id;
  useEffect(() => {
    setLocalMappedEntity(bankAccount?.mappedInvestmentEntities?.[0] ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bankAccountId]);

  const { mapBankAccountToEntity, loading: savingMapping } = useMapBankAccountToEntity();
  const { configureBankAccount, loading: configuringLoading } = useConfigureBankAccount();
  const { providers, loading: providersLoading } = useGetAllProvidersWithCapabilities();

  const fidesBankSources = useMemo<ProviderBankSource[]>(
    () => getBankSourcesByProvider(providers, "fides"),
    [providers],
  );

  const rpaBankSources = useMemo<ProviderBankSource[]>(
    () => getBankSourcesByProvider(providers, "rpa"),
    [providers],
  );

  const isLoadingProviders = providersLoading;

  const initialFormValues = useMemo<SlideoutFormState>(() => {
    if (!bankAccount) {
      return getInitialFormState();
    }

    const reportingEntityId = bankAccount.mappedInvestmentEntities?.[0]?.entityId ?? null;

    const configWithUdf = bankAccount.configurations?.find((c) => {
      const config = c as { fundRecsUdf?: string };
      return config.fundRecsUdf != null && config.fundRecsUdf !== "";
    });
    const existingUdf =
      (configWithUdf as { fundRecsUdf?: string } | undefined)?.fundRecsUdf || "";

    const existingFidesProvider = bankAccount.providers?.find(
      (p) => p.providerName?.toLowerCase() === "fides",
    );
    const existingRpaProvider = bankAccount.providers?.find(
      (p) => p.providerName?.toLowerCase() === "rpa",
    );

    const existingFidesBankMapping = findMatchingBankSource(
      existingFidesProvider?.bankSource,
      fidesBankSources,
    );
    const existingRpaBankMapping = findMatchingBankSource(
      existingRpaProvider?.bankSource,
      rpaBankSources,
    );

    const fidesVerification = bankAccount.verifications?.find(
      (v) => v.providerId === existingFidesProvider?.providerId,
    );
    const existingFidesVerificationStatus = (fidesVerification?.verificationStatus ||
      "UNVERIFIED") as VerificationStatus;

    return {
      selectedEntityId: reportingEntityId,
      fidesEnabled: bankAccount.fidesEnabled,
      fidesBankMapping: existingFidesBankMapping,
      fidesVerificationStatus: existingFidesVerificationStatus,
      rpaEnabled: bankAccount.rpaEnabled,
      rpaBankMapping: existingRpaBankMapping,
      fundRecsEnabled: bankAccount.fundRecsEnabled,
      fundRecsUdf: existingUdf,
    };
  }, [bankAccount, fidesBankSources, rpaBankSources]);

  const handleOpenEntityModal = useCallback(() => {
    setIsEntityModalOpen(true);
  }, []);

  const handleEntityModalClose = useCallback(() => {
    setIsEntityModalOpen(false);
  }, []);

  // Handle entity modal save - calls mutation immediately when user confirms in modal
  const handleEntityModalSave = useCallback(
    async (entityId: number | null, entityName: string): Promise<boolean> => {
      if (!bankAccount || entityId === null || !arenaId) return false;

      const entitySuccess = await mapBankAccountToEntity({
        bankAccountId: bankAccount.id,
        entityId: toGlobalId("AccountObjectType", entityId),
        arenaId: toGlobalId("ArenaObjectType", arenaId),
      });

      if (entitySuccess) {
        toast.success("Entity association updated successfully");
        setIsEntityModalOpen(false);
        const savedEntity = { entityId, entityName };
        setLocalMappedEntity(savedEntity);
        onEntitySave?.(savedEntity);
        return true;
      } else {
        addError(formatError("mapBankAccountToEntity returned false"));
        toast.error("Failed to update entity association. Please try again.");
        return false;
      }
    },
    [bankAccount, mapBankAccountToEntity, onEntitySave, arenaId],
  );

  const handleSave = useCallback(
    async (formValues: SlideoutFormState) => {
      if (!bankAccount) return;

      // Merge formValues with initialFormValues to get complete state
      // formValues may only contain changed fields, so we need to merge with initial values
      const completeFormValues: SlideoutFormState = {
        ...initialFormValues,
        ...formValues,
      };

      // Build arrays based on current form values
      const currentProviders = buildProvidersArray(
        completeFormValues,
        fidesBankSources,
        rpaBankSources,
      );
      const currentVerifications = buildVerificationsArray(
        completeFormValues,
        fidesBankSources,
      );
      const currentProviderIds = currentProviders.map((p) => p.providerId);
      const currentConfigurations = buildConfigurationsArray(
        completeFormValues,
        currentProviderIds,
        providers,
      );

      // Build arrays based on initial form values to compare
      const initialProviders = buildProvidersArray(
        initialFormValues,
        fidesBankSources,
        rpaBankSources,
      );
      const initialVerifications = buildVerificationsArray(initialFormValues, fidesBankSources);
      const initialProviderIds = initialProviders.map((p) => p.providerId);
      const initialConfigurations = buildConfigurationsArray(
        initialFormValues,
        initialProviderIds,
        providers,
      );

      // Deep comparison to detect actual changes
      const providersChanged = !isEqual(currentProviders, initialProviders);
      const verificationsChanged = !isEqual(currentVerifications, initialVerifications);
      const configurationsChanged = !isEqual(currentConfigurations, initialConfigurations);

      if (!providersChanged && !verificationsChanged && !configurationsChanged) {
        toast.success("Changes have been successfully saved");
        if (onSave) {
          onSave(formValues);
        }
        onClose();
        return;
      }

      // Send the actual arrays if changed, null if not changed
      const providersPayload = providersChanged ? currentProviders : null;
      const verifications = verificationsChanged ? currentVerifications : null;
      const configurations = configurationsChanged ? currentConfigurations : null;

      const configSuccess = await configureBankAccount({
        bankAccountId: bankAccount.bankAccountId,
        providers: providersPayload,
        verifications,
        configurations,
      });

      if (!configSuccess) {
        addError(formatError("configureBankAccount returned false"));
        toast.error("Failed to save configuration. Please try again.");
        return;
      }

      toast.success("Changes have been successfully saved");
      if (onSave) {
        onSave(formValues);
      }
      onClose();
    },
    [
      bankAccount,
      initialFormValues,
      configureBankAccount,
      fidesBankSources,
      rpaBankSources,
      providers,
      onClose,
      onSave,
    ],
  );

  return {
    initialFormValues,
    localMappedEntity,
    isEntityModalOpen,
    handleOpenEntityModal,
    handleEntityModalClose,
    handleEntityModalSave,
    isEntityModalSaving: savingMapping,
    fidesBankSources,
    rpaBankSources,
    isSaving: configuringLoading,
    isLoadingProviders,
    handleSave,
  };
};
